import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Vote, Share2, Trophy, ArrowLeft } from "lucide-react";
import api, { apiErr, FILE_BASE } from "@/lib/api";

export default function CandidatePage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [voted, setVoted] = useState(false);

  const load = () => api.get(`/candidates/${slug}`).then((r) => setC(r.data)).catch(() => setC(false));
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [slug]);
  useEffect(() => { setVoted(!!localStorage.getItem(`cc_voted_${slug}`)); }, [slug]);

  const vote = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/candidates/${slug}/vote`);
      setC((p) => ({ ...p, votes: data.votes }));
      localStorage.setItem(`cc_voted_${slug}`, "1");
      setVoted(true);
      toast.success("Vote enregistré ! Merci pour votre soutien 🇫🇷");
    } catch (e) { toast.error(apiErr(e)); setVoted(true); }
    finally { setBusy(false); }
  };

  const share = async () => {
    const url = window.location.href;
    const text = `Je soutiens ${c.display_name} — ${c.edition_name || "COCARDE"} COCARDE Pizza ! Votez 👉 #cocardepizza`;
    if (navigator.share) { try { await navigator.share({ title: "COCARDE Pizza", text, url }); return; } catch { /* ignore */ } }
    navigator.clipboard.writeText(`${text} ${url}`);
    toast.success("Lien copié ! Partagez avec #cocardepizza");
  };

  if (c === false) return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="font-display text-3xl cc-navy">Candidat introuvable</h1><Link to="/concours" className="text-cc-red font-bold mt-4 inline-block">Retour aux élections</Link></div>;
  if (!c) return <div className="min-h-[50vh] grid place-items-center font-display text-2xl cc-navy">Chargement…</div>;

  const pending = c.status !== "approved";
  const edClosed = c.edition_status && c.edition_status !== "open";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to={`/concours/${c.edition || ""}`} data-testid="candidate-back" className="text-slate-500 font-bold inline-flex items-center gap-1 hover:text-cc-red"><ArrowLeft size={16} /> Retour à l'édition {c.edition_name || ""}</Link>

      <div className="grid md:grid-cols-2 gap-8 mt-6">
        <div className="rounded-3xl overflow-hidden bg-black aspect-square">
          {c.video_url ? (
            <video src={`${FILE_BASE}${c.video_url}`} poster={c.poster_url ? `${FILE_BASE}${c.poster_url}` : undefined} preload="metadata" controls playsInline className="w-full h-full object-cover" data-testid="candidate-video" />
          ) : <div className="grid place-items-center h-full text-white/50">Vidéo indisponible</div>}
        </div>

        <div>
          {pending ? (
            <span className="inline-block bg-amber-100 text-amber-700 font-bold text-sm rounded-full px-4 py-1.5">En attente de validation</span>
          ) : (
            <span className="inline-flex items-center gap-2 bg-cc-navy text-white font-bold text-sm rounded-full px-4 py-1.5">
              <Trophy size={15} className="text-cc-gold" /> {c.rank}{c.rank === 1 ? "er" : "e"} sur {c.total_candidates} candidats
            </span>
          )}
          <h1 className="font-display text-4xl sm:text-5xl cc-navy mt-4" data-testid="candidate-name">{c.display_name}</h1>
          <div className="mt-4 card-cocarde p-5">
            <p className="eyebrow text-cc-blue mb-2">Le programme</p>
            <p className="text-slate-700">{c.program}</p>
          </div>

          <div className="flex items-baseline gap-2 mt-6">
            <span className="font-display text-6xl text-cc-red" data-testid="candidate-votes">{c.votes}</span>
            <span className="text-slate-500 font-bold">votes</span>
          </div>

          <button data-testid="candidate-vote-btn" onClick={vote} disabled={busy || voted || pending || edClosed}
            className={`btn-cocarde w-full mt-4 inline-flex items-center justify-center gap-2 ${!voted && !pending && !edClosed ? "pulse-vote" : ""} disabled:opacity-60`}>
            <Vote size={18} /> {c.edition_status === "closed" ? "Votes clôturés" : voted ? "Vous avez voté" : pending || edClosed ? "Bientôt disponible" : "Voter pour ce candidat"}
          </button>
          <button data-testid="candidate-share-btn" onClick={share} className="btn-navy w-full mt-3 inline-flex items-center justify-center gap-2">
            <Share2 size={18} /> Partager avec #cocardepizza
          </button>
        </div>
      </div>
    </div>
  );
}
